import { useCallback, useState } from "react";

import { useDispatch } from "react-redux";

import useSingleRevealContactWithUpdateQuery from "app/pages/sales-intelligence/sub-modules/contacts-search/query/queries/useContactsSingleInfoQueries";

import { addRevealingContactIds, removeRevealingContactIds } from "../../common/store/action-creators";
import type { Integration } from "../../management/types";
import { hideExportSuccessToasts } from "../helpers/exportFlow";
import { setExportFlowOrigin } from "../store/action-creators";
import type { ContactSearchEntry, CRMEntity } from "../types/common";

type RevealBeforeExportAction = (contact: ContactSearchEntry, integration: Integration, as: CRMEntity) => void;

type UseRevealBeforeExportResult = {
    tryExportOrReveal: RevealBeforeExportAction;
    isRevealModalOpen: boolean;
    onConfirmReveal: () => void;
    onCloseRevealModal: () => void;
};

export function useRevealBeforeExport(
    onExport: (contact: ContactSearchEntry, integration: Integration, as: CRMEntity) => void,
): UseRevealBeforeExportResult {
    const dispatch = useDispatch();
    const revealMutation = useSingleRevealContactWithUpdateQuery();

    const [pendingReveal, setPendingReveal] = useState<{
        contact: ContactSearchEntry;
        integration: Integration;
        as: CRMEntity;
    } | null>(null);

    const tryExportOrReveal = useCallback(
        (contact: ContactSearchEntry, integration: Integration, as: CRMEntity) => {
            dispatch(hideExportSuccessToasts());
            if (contact.isRevealed) {
                onExport(contact, integration, as);
            } else {
                setPendingReveal({ contact, integration, as });
            }
        },
        [onExport],
    );

    const onCloseRevealModal = useCallback(() => {
        setPendingReveal(null);
    }, []);

    const onConfirmReveal = useCallback(async () => {
        if (!pendingReveal) {
            return;
        }

        const { contact, integration, as } = pendingReveal;
        setPendingReveal(null);
        dispatch(setExportFlowOrigin("row"));
        dispatch(addRevealingContactIds([contact.id]));

        try {
            const revealResult = await revealMutation.mutateAsync(contact);

            const revealedContact: ContactSearchEntry = {
                ...contact,
                isRevealed: revealResult.isRevealed,
                privateData: revealResult.revealedInfo,
            };

            onExport(revealedContact, integration, as);
        } finally {
            dispatch(removeRevealingContactIds([contact.id]));
        }
    }, [pendingReveal, revealMutation, onExport]);

    return {
        tryExportOrReveal,
        isRevealModalOpen: !!pendingReveal,
        onConfirmReveal,
        onCloseRevealModal,
    };
}
